import React, { useEffect, useMemo, useRef, useState } from "react";
import { BoardApi } from "../../../services/board-service-api";
import { KModal } from "../../ui-kit/k-modal/k-modal";
import KuikaButton, {
  KUIKA_BUTTON_ERROR_HOLD_MS,
  type IKuikaButtonStatusLabels,
  type KuikaButtonStatus
} from "../../ui-kit/kuika-button/kuika-button";
import KuikaSelectbox from "../../ui-kit/kuika-selectbox/kuika-selectbox";
import { toastService } from "../../ui-kit/kuika-toast/toast-service";
import KuikaH5 from "../../ui-kit/typography/H5/H5";
import "./board-import.scss";
import { normalizePriority, normalizeType } from "./import-parsers";
import { CsvColumnMapping, ImportIssuePayload, ImportResult, ImportRow, ImportSource } from "./import-types";
import { CsvImporter, EmptyImportError, getImporter, IMPORTERS, Importer, ParsedImport } from "./importers";
import { DirectoryMember, displayNameOf, getMembers } from "./people-directory";
import { BoardStatus, ISSUE_TYPE_LABELS, IssueType, Project } from "./types";
import { useBoardConfig } from "./use-board";

export interface BoardImportWizardProps {
  defaultProjectKey?: string;
  onClose: () => void;
}

const TYPE_OPTIONS: IssueType[] = ["task", "feature", "bug", "chore", "epic"];
const UNASSIGNED: string = "__unassigned__";

/** What the import button says while the issues are on their way in. */
const IMPORT_STATUS_LABELS: IKuikaButtonStatusLabels = { loading: "Importing", error: "Try again" };

// Distinct, non-empty raw values in source order.
function distinct(values: string[]): string[] {
  const seen: Set<string> = new Set<string>();
  const out: string[] = [];
  for (const v of values) {
    const t: string = v.trim();
    if (t && !seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  }
  return out;
}

const BoardImportWizard: React.FC<BoardImportWizardProps> = ({ defaultProjectKey, onClose }) => {
  const { config } = useBoardConfig();
  const members: DirectoryMember[] = getMembers();

  const [source, setSource] = useState<ImportSource>("jira");
  const [rows, setRows] = useState<ImportRow[]>([]);
  const [fileName, setFileName] = useState<string>("");
  const [dragging, setDragging] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [headers, setHeaders] = useState<string[]>([]);
  const [grid, setGrid] = useState<string[][]>([]);
  const [mapping, setMapping] = useState<CsvColumnMapping | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const [projectKey, setProjectKey] = useState<string>(defaultProjectKey ?? config.projects[0]?.key ?? "");
  const [statusMap, setStatusMap] = useState<Record<string, string>>({});
  const [typeMap, setTypeMap] = useState<Record<string, IssueType>>({});
  const [assigneeMap, setAssigneeMap] = useState<Record<string, string>>({});
  const [importStatus, setImportStatus] = useState<KuikaButtonStatus>("idle");
  const [result, setResult] = useState<ImportResult | null>(null);
  const importing: boolean = importStatus === "loading";

  // "Try again" is held long enough to read, then the button is ready for another go. The inline
  // error under the dropzone is what stays.
  useEffect((): (() => void) | undefined => {
    if (importStatus !== "error") {
      return undefined;
    }
    const timer: number = window.setTimeout((): void => setImportStatus("idle"), KUIKA_BUTTON_ERROR_HOLD_MS);
    return (): void => window.clearTimeout(timer);
  }, [importStatus]);

  const projectStatuses: BoardStatus[] = useMemo(
    () => config.statuses.filter((s: BoardStatus) => s.projectKey === projectKey),
    [config.statuses, projectKey]
  );

  // Seed the status/type/assignee maps from a freshly parsed set of rows: match by name where we can.
  const seedMaps = (parsed: ImportRow[], statuses: BoardStatus[]): void => {
    const firstStatus: string = statuses[0]?.id ?? "";
    const sMap: Record<string, string> = {};
    for (const raw of distinct(parsed.map((r: ImportRow) => r.status))) {
      const hit: BoardStatus | undefined = statuses.find(
        (s: BoardStatus) => s.name.toLowerCase() === raw.toLowerCase()
      );
      sMap[raw] = hit?.id ?? firstStatus;
    }
    const tMap: Record<string, IssueType> = {};
    for (const raw of distinct(parsed.map((r: ImportRow) => r.type))) tMap[raw] = normalizeType(raw);
    const aMap: Record<string, string> = {};
    for (const raw of distinct(parsed.map((r: ImportRow) => r.assignee))) {
      const hit: DirectoryMember | undefined = members.find(
        (m: DirectoryMember) => m.name.toLowerCase() === raw.toLowerCase()
      );
      aMap[raw] = hit?.id ?? UNASSIGNED;
    }
    setStatusMap(sMap);
    setTypeMap(tMap);
    setAssigneeMap(aMap);
  };

  const onFile = (file: File): void => {
    setParseError(null);
    setResult(null);
    setFileName(file.name);
    const reader: FileReader = new FileReader();
    reader.onload = (): void => {
      const text: string = String(reader.result ?? "");
      try {
        const parsed: ParsedImport = getImporter(source).parse(text);
        setHeaders(parsed.remap?.headers ?? []);
        setGrid(parsed.remap?.grid ?? []);
        setMapping(parsed.remap?.mapping ?? null);
        setRows(parsed.rows);
        seedMaps(
          parsed.rows,
          config.statuses.filter((s: BoardStatus) => s.projectKey === projectKey)
        );
      } catch (e: unknown) {
        if (e instanceof EmptyImportError) setParseError("That file has no data rows.");
        else setParseError("Couldn't parse that file. Check it's the right export for the selected source.");
      }
    };
    reader.readAsText(file);
  };

  // Re-apply a hand-edited column mapping to the already-loaded CSV grid. Only CSV sources remap.
  const updateMapping = (next: CsvColumnMapping): void => {
    const importer: Importer = getImporter(source);
    if (!(importer instanceof CsvImporter)) return;
    setMapping(next);
    const parsed: ImportRow[] = importer.applyMapping(grid, next);
    setRows(parsed);
    seedMaps(parsed, projectStatuses);
  };

  const epicCount: number = rows.filter((r: ImportRow) => normalizeType(r.type) === "epic").length;
  const subtaskCount: number = rows.filter((r: ImportRow) => r.parentKey.trim().length > 0).length;

  const runImport = (): void => {
    if (importing || rows.length === 0 || !projectKey) return;
    const firstStatus: string = projectStatuses[0]?.id ?? "";
    const payload: ImportIssuePayload[] = rows.map((r: ImportRow) => ({
      externalKey: r.externalKey,
      summary: r.summary,
      description: r.description,
      type: typeMap[r.type] ?? normalizeType(r.type),
      statusId: statusMap[r.status] ?? firstStatus,
      priority: normalizePriority(r.priority),
      assigneeUserId:
        assigneeMap[r.assignee] && assigneeMap[r.assignee] !== UNASSIGNED ? assigneeMap[r.assignee] : null,
      labels: r.labels,
      storyPoints: r.storyPoints,
      epicKey: r.epicKey,
      parentKey: r.parentKey,
      createdAt: r.createdAt
    }));
    setImportStatus("loading");
    // A long job the user is free to walk away from, so the toast carries the outcome; the wizard's
    // own result step is the detail (warnings, counts) for whoever stayed.
    const work: Promise<ImportResult> = BoardApi.importIssues(projectKey, payload);
    toastService.promise(work, {
      loading: `Importing ${rows.length} issues…`,
      success: (imported: ImportResult): string => `${imported.created} issues imported.`,
      error: "Import failed. Please try again."
    });
    void work
      .then((res: ImportResult): void => {
        setImportStatus("idle");
        setResult(res);
      })
      .catch((): void => {
        setImportStatus("error");
        setParseError("Import failed. Please try again.");
      });
  };

  const columnField = (label: string, value: number | null, onChange: (v: number | null) => void): React.ReactNode => (
    <label className="kuika-import__field">
      <span className="kuika-import__field-label">{label}</span>
      <KuikaSelectbox
        size="sm"
        dropdownMatchSelectWidth={false}
        value={value === null ? "" : String(value)}
        onChange={(v: string | number | null) => onChange(v === "" || v === null ? null : Number(v))}
        options={[
          { value: "", label: "—" },
          ...headers.map((h: string, i: number) => ({ value: String(i), label: h }))
        ]}
      />
    </label>
  );

  const activeImporter: Importer = getImporter(source);

  /**
   * Two steps, not three: the source picker and the column/status mapping are ONE body that grows —
   * the mapping is appended under the same dropzone rather than replacing it. Morphing between them
   * would cross-fade the dropzone with an identical dropzone, and the leaving copy's `<input
   * type="file">` would null `fileInputRef` on its way out, leaving "click to browse" dead.
   */
  const stepKey: string = result ? "result" : "pick";

  return (
    <KModal
      showModal
      closeModal={onClose}
      centered
      width={560}
      header={<KuikaH5>Import issues</KuikaH5>}
      stepKey={stepKey}
    >
      <div className="kuika-import">
        {result ? (
          <div className="kuika-import__body">
            <div className="kuika-import__result">
              <div className="kuika-import__result-num">{result.created}</div>
              <div>
                issues imported{result.epics > 0 ? ` · ${result.epics} epics` : ""}
                {result.subtasks > 0 ? ` · ${result.subtasks} sub-tasks` : ""}
              </div>
            </div>
            {result.warnings.length > 0 && (
              <ul className="kuika-import__warnings">
                {result.warnings.slice(0, 8).map((w: string, i: number) => (
                  <li key={i}>{w}</li>
                ))}
                {result.warnings.length > 8 && <li>…and {result.warnings.length - 8} more</li>}
              </ul>
            )}
            <div className="kuika-import__actions">
              <KuikaButton type="primary" onClick={onClose}>
                Done
              </KuikaButton>
            </div>
          </div>
        ) : (
          <div className="kuika-import__body">
            <div className="kuika-import__sources">
              {IMPORTERS.map((imp: Importer) => (
                <button
                  key={imp.source}
                  type="button"
                  className={`kuika-import__source${source === imp.source ? " kuika-import__source--active" : ""}`}
                  onClick={() => {
                    setSource(imp.source);
                    setRows([]);
                    setHeaders([]);
                    setMapping(null);
                    setParseError(null);
                    setFileName("");
                  }}
                >
                  {imp.label}
                </button>
              ))}
            </div>
            <div className="kuika-import__hint">{activeImporter.hint}</div>

            <div
              className={`kuika-import__drop${dragging ? " kuika-import__drop--over" : ""}${fileName ? " kuika-import__drop--has-file" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragOver={(e: React.DragEvent) => {
                e.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e: React.DragEvent) => {
                e.preventDefault();
                setDragging(false);
                const file: File | undefined = e.dataTransfer.files?.[0];
                if (file) onFile(file);
              }}
            >
              <svg
                className="kuika-import__drop-icon"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M12 15V4m0 0L8 8m4-4 4 4"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"
                  stroke="currentColor"
                  strokeWidth="1.6"
                  strokeLinecap="round"
                />
              </svg>
              <div className="kuika-import__drop-file">
                <strong>{fileName || `Drop your ${activeImporter.fileNoun} here`}</strong>
                <span>{fileName ? "click or drop to replace" : "or click to browse"}</span>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept={activeImporter.accept}
                style={{ display: "none" }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const file: File | undefined = e.target.files?.[0];
                  if (file) onFile(file);
                }}
              />
            </div>
            {parseError && <div className="kuika-import__error">{parseError}</div>}

            {rows.length > 0 && (
              <>
                <div className="kuika-import__summary">
                  <strong>{rows.length}</strong> rows · <strong>{epicCount}</strong> epics ·{" "}
                  <strong>{subtaskCount}</strong> sub-tasks
                </div>

                <div className="kuika-import__row">
                  <span className="kuika-import__field-label">Import into</span>
                  <KuikaSelectbox
                    size="sm"
                    dropdownMatchSelectWidth={false}
                    value={projectKey}
                    onChange={(v: string | number | null) => setProjectKey(v ? String(v) : "")}
                    options={config.projects.map((p: Project) => ({ value: p.key, label: `${p.key} · ${p.name}` }))}
                  />
                </div>

                {mapping && headers.length > 0 && (
                  <details className="kuika-import__group">
                    <summary>Columns</summary>
                    <div className="kuika-import__fields">
                      {columnField("Summary", mapping.summary, (v) => updateMapping({ ...mapping, summary: v }))}
                      {columnField("Description", mapping.description, (v) =>
                        updateMapping({ ...mapping, description: v })
                      )}
                      {columnField("Type", mapping.type, (v) => updateMapping({ ...mapping, type: v }))}
                      {columnField("Status", mapping.status, (v) => updateMapping({ ...mapping, status: v }))}
                      {columnField("Priority", mapping.priority, (v) => updateMapping({ ...mapping, priority: v }))}
                      {columnField("Assignee", mapping.assignee, (v) => updateMapping({ ...mapping, assignee: v }))}
                      {columnField("Story points", mapping.storyPoints, (v) =>
                        updateMapping({ ...mapping, storyPoints: v })
                      )}
                      {columnField("Epic link", mapping.epicKey, (v) => updateMapping({ ...mapping, epicKey: v }))}
                      {columnField("Parent", mapping.parentKey, (v) => updateMapping({ ...mapping, parentKey: v }))}
                      {columnField("Key", mapping.externalKey, (v) => updateMapping({ ...mapping, externalKey: v }))}
                    </div>
                  </details>
                )}

                {distinct(rows.map((r: ImportRow) => r.status)).length > 0 && (
                  <details className="kuika-import__group" open>
                    <summary>
                      Statuses → {config.projects.find((p: Project) => p.key === projectKey)?.name ?? "project"}
                    </summary>
                    <div className="kuika-import__fields">
                      {distinct(rows.map((r: ImportRow) => r.status)).map((raw: string) => (
                        <label key={raw} className="kuika-import__field">
                          <span className="kuika-import__field-label">{raw || "(blank)"}</span>
                          <KuikaSelectbox
                            size="sm"
                            dropdownMatchSelectWidth={false}
                            value={statusMap[raw] ?? ""}
                            onChange={(v: string | number | null) =>
                              setStatusMap((m) => ({ ...m, [raw]: v ? String(v) : "" }))
                            }
                            options={projectStatuses.map((s: BoardStatus) => ({ value: s.id, label: s.name }))}
                          />
                        </label>
                      ))}
                    </div>
                  </details>
                )}

                <details className="kuika-import__group">
                  <summary>Types</summary>
                  <div className="kuika-import__fields">
                    {distinct(rows.map((r: ImportRow) => r.type)).map((raw: string) => (
                      <label key={raw} className="kuika-import__field">
                        <span className="kuika-import__field-label">{raw || "(blank)"}</span>
                        <KuikaSelectbox
                          size="sm"
                          dropdownMatchSelectWidth={false}
                          value={typeMap[raw] ?? "task"}
                          onChange={(v: string | number | null) =>
                            setTypeMap((m) => ({ ...m, [raw]: (v ? String(v) : "task") as IssueType }))
                          }
                          options={TYPE_OPTIONS.map((t: IssueType) => ({ value: t, label: ISSUE_TYPE_LABELS[t] }))}
                        />
                      </label>
                    ))}
                  </div>
                </details>

                {distinct(rows.map((r: ImportRow) => r.assignee)).length > 0 && (
                  <details className="kuika-import__group">
                    <summary>Assignees</summary>
                    <div className="kuika-import__fields">
                      {distinct(rows.map((r: ImportRow) => r.assignee)).map((raw: string) => (
                        <label key={raw} className="kuika-import__field">
                          <span className="kuika-import__field-label">{raw}</span>
                          <KuikaSelectbox
                            size="sm"
                            searchable
                            dropdownMatchSelectWidth={false}
                            value={assigneeMap[raw] ?? UNASSIGNED}
                            onChange={(v: string | number | null) =>
                              setAssigneeMap((m) => ({ ...m, [raw]: v ? String(v) : UNASSIGNED }))
                            }
                            options={[
                              { value: UNASSIGNED, label: "Unassigned" },
                              ...members.map((mem: DirectoryMember) => ({
                                value: mem.id,
                                label: displayNameOf(mem.id)
                              }))
                            ]}
                          />
                        </label>
                      ))}
                    </div>
                  </details>
                )}

                <div className="kuika-import__actions">
                  <KuikaButton type="tertiary" color="neutral" onClick={onClose}>
                    Cancel
                  </KuikaButton>
                  <KuikaButton
                    type="primary"
                    onClick={runImport}
                    disabled={!projectKey}
                    status={importStatus}
                    statusLabels={IMPORT_STATUS_LABELS}
                  >
                    {`Import ${rows.length} issues`}
                  </KuikaButton>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </KModal>
  );
};

export default BoardImportWizard;
